// =============================================================================
// MFAChallenge — post-login TOTP / recovery code prompt for desktop sellers
// + customers who have opted into 2FA.
// Two modes:
//   - "totp"      → 6-digit code from authenticator app (promotes session to AAL2)
//   - "recovery"  → one of the 10 single-use recovery codes (stays at AAL1, but
//                    proves possession of a server-issued single-use code)
//
// Mirrors kapio-admin/src/components/AdminMFAChallenge.jsx but desktop-styled.
// Rendered by App.jsx between login and dashboard when the user has a verified
// TOTP factor and the current session AAL < aal2.
// =============================================================================

import { useState } from "react";
import { ShieldCheck } from "lucide-react";
import { C } from "../lib/theme";
import { KapioLogo } from "./KapioLogo";
import { supabase } from "../lib/supabase";

export function MFAChallenge({ factorId, onVerified, onCancel }) {
  const [mode, setMode] = useState("totp"); // totp | recovery
  const [totp, setTotp] = useState("");
  const [recovery, setRecovery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Promise.race timeout — supabase-js challengeAndVerify has been observed
  // to hang in this codebase (see PR #37); 20s defensive timeout surfaces a
  // real error instead of a forever spinner.
  const withTimeout = (p, ms, label) =>
    Promise.race([
      p,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms)
      ),
    ]);

  const verifyTotp = async () => {
    if (totp.length !== 6) return;
    setLoading(true);
    setError("");
    let vErr = null;
    try {
      console.log("[Login MFA] desktop verifyTotp factorId=", factorId);
      const result = await withTimeout(
        supabase.auth.mfa.challengeAndVerify({ factorId, code: totp }),
        20000,
        "challengeAndVerify"
      );
      vErr = result?.error ?? null;
      console.log("[Login MFA] desktop challengeAndVerify resolved, vErr=", vErr);
    } catch (err) {
      console.error("[Login MFA] desktop challengeAndVerify threw/timed out:", err);
      vErr = err;
    }
    setLoading(false);
    if (vErr) {
      const msg = vErr?.message || "";
      setError(
        msg.includes("timed out")
          ? "Verification didn't complete. Check your connection and try again."
          : "Invalid code. Try again."
      );
      return;
    }
    onVerified();
  };

  const verifyRecovery = async () => {
    const trimmed = recovery.trim();
    if (!trimmed) {
      setError("Enter a recovery code.");
      return;
    }
    setLoading(true);
    setError("");
    let data = null;
    let rErr = null;
    try {
      const result = await withTimeout(
        supabase.rpc("consume_recovery_code", { p_code: trimmed }),
        20000,
        "consume_recovery_code"
      );
      data = result?.data;
      rErr = result?.error ?? null;
    } catch (err) {
      console.error("[Login MFA] desktop consume_recovery_code threw/timed out:", err);
      rErr = err;
    }
    setLoading(false);
    // Don't reveal whether the code is unknown vs already-used — both surface
    // as "Invalid recovery code." to avoid an oracle for valid-but-used codes.
    if (rErr || data !== true) {
      const msg = rErr?.message || "";
      setError(
        msg.includes("timed out")
          ? "Verification didn't complete. Check your connection and try again."
          : "Invalid recovery code."
      );
      return;
    }
    onVerified();
  };

  const submit = () => {
    if (mode === "totp") verifyTotp();
    else verifyRecovery();
  };

  const switchMode = () => {
    setMode(mode === "totp" ? "recovery" : "totp");
    setTotp("");
    setRecovery("");
    setError("");
  };

  const canSubmit = !loading && (mode === "totp" ? totp.length === 6 : !!recovery.trim());

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: C.bg,
        fontFamily: "'Outfit',-apple-system,sans-serif",
        padding: "24px 16px",
      }}
    >
      <div style={{ width: 420, maxWidth: "100%" }}>
        <div
          style={{
            textAlign: "center",
            marginBottom: 24,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <KapioLogo size={36} />
            <span style={{ fontSize: 22, fontWeight: 900, color: C.hunter, letterSpacing: "-0.5px" }}>
              Kapio
            </span>
          </div>
          <p style={{ fontSize: 12, color: C.muted, margin: "8px 0 0" }}>
            Two-factor authentication
          </p>
        </div>

        <div
          style={{
            background: C.white,
            borderRadius: 20,
            padding: "28px 24px",
            border: "1px solid rgba(196,193,183,0.35)",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: 14 }}>
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 16,
                background: C.greenBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: 10,
              }}
            >
              <ShieldCheck size={28} color={C.hunter} />
            </div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 800,
                color: C.charcoal,
                margin: "0 0 4px",
                textAlign: "center",
              }}
            >
              {mode === "totp" ? "Enter the 6-digit code" : "Enter a recovery code"}
            </h2>
            <p
              style={{
                fontSize: 13,
                color: C.muted,
                margin: 0,
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              {mode === "totp"
                ? "Open your authenticator app and enter the code shown."
                : "Use one of the recovery codes you saved during setup."}
            </p>
          </div>

          {mode === "totp" ? (
            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  color: C.slate,
                  display: "block",
                  marginBottom: 6,
                  letterSpacing: 0.2,
                }}
              >
                Authenticator code
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={totp}
                onChange={(e) => {
                  setTotp(e.target.value.replace(/\D/g, "").slice(0, 6));
                  if (error) setError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && canSubmit && submit()}
                placeholder="000000"
                autoFocus
                maxLength={6}
                aria-label="6-digit authenticator code"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(196,193,183,0.4)",
                  fontSize: 18,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  letterSpacing: 4,
                  textAlign: "center",
                  outline: "none",
                  background: C.cream,
                  boxSizing: "border-box",
                }}
              />
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  fontSize: 11.5,
                  fontWeight: 800,
                  color: C.slate,
                  display: "block",
                  marginBottom: 6,
                  letterSpacing: 0.2,
                }}
              >
                Recovery code
              </label>
              <input
                type="text"
                value={recovery}
                onChange={(e) => {
                  setRecovery(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 16));
                  if (error) setError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && canSubmit && submit()}
                placeholder="ABC1234XYZ"
                autoFocus
                autoComplete="off"
                autoCapitalize="characters"
                maxLength={16}
                aria-label="Recovery code"
                style={{
                  width: "100%",
                  padding: "12px 14px",
                  borderRadius: 12,
                  border: "1px solid rgba(196,193,183,0.4)",
                  fontSize: 15,
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                  letterSpacing: 1,
                  outline: "none",
                  background: C.cream,
                  boxSizing: "border-box",
                }}
              />
            </div>
          )}

          {error && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                background: C.redBg,
                border: `1px solid ${C.red}`,
                borderRadius: 10,
                fontSize: 12.5,
                color: C.red,
                fontWeight: 600,
                marginBottom: 14,
              }}
            >
              {error}
            </div>
          )}

          <button
            onClick={submit}
            disabled={!canSubmit}
            style={{
              width: "100%",
              padding: "13px 0",
              borderRadius: 14,
              background: !canSubmit ? "rgba(196,193,183,0.6)" : C.hunter,
              color: !canSubmit ? C.muted : C.white,
              border: "none",
              fontSize: 15,
              fontWeight: 700,
              cursor: !canSubmit ? "not-allowed" : "pointer",
              fontFamily: "inherit",
              marginBottom: 12,
            }}
          >
            {loading ? "Verifying…" : "Verify"}
          </button>

          <button
            type="button"
            onClick={switchMode}
            style={{
              background: "none",
              border: "none",
              color: C.hunter,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              padding: "4px 0",
              fontFamily: "inherit",
              display: "block",
              width: "100%",
              textAlign: "center",
            }}
          >
            {mode === "totp" ? "Use a recovery code instead" : "Back to authenticator code"}
          </button>

          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              style={{
                background: "none",
                border: "none",
                color: C.muted,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: "pointer",
                padding: "8px 0 0",
                fontFamily: "inherit",
                display: "block",
                width: "100%",
                textAlign: "center",
              }}
            >
              Cancel and sign out
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
